import { getApiData, getApiTable, pickApiRowValue } from "./qmsApiUtils";

export const QMS_DASHBOARD_PERIOD_PRESETS = [
  "Today",
  "This Week",
  "This Month",
  "This Quarter",
  "This Year",
  "Last Week",
  "Last Month",
  "Last Quarter",
  "Last Year",
  "Last 7 Days",
  "Last 30 Days",
  "Custom",
];

export const QMS_CC_TABS = [
  { id: "C", label: "Command center", hero: true },
  { id: "1", label: "By document type" },
  { id: "2", label: "By category" },
  { id: "3", label: "Pending focus" },
  { id: "4", label: "By staff role" },
  { id: "5", label: "By staff" },
];

export const QMS_DATE_PRESETS = QMS_DASHBOARD_PERIOD_PRESETS.map((value) => ({
  label: value,
  value,
}));

export const DEFAULT_QMS_DASHBOARD_RANGE = {
  preset: "This Week",
  startDate: "",
  endDate: "",
};

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toIsoDate(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function startOfQuarter(date) {
  return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);
}

function endOfQuarter(date) {
  return new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3 + 3, 0);
}

/** Same period logic as DateRangeFilter / legacy DMS dashboard. */
export function resolveQmsDashboardDateRange(value = DEFAULT_QMS_DASHBOARD_RANGE) {
  const preset = String(value?.preset || value?.dateRange || "This Week").trim();
  const today = startOfDay(new Date());

  if (preset === "Custom") {
    const start = value?.startDate || value?.dateFrom || "";
    const end = value?.endDate || value?.dateTo || "";
    if (start && end) return { startDate: start, endDate: end, preset };
  }

  const yesterday = addDays(today, -1);
  const thisWeekStart = addDays(today, -today.getDay());
  const lastWeekStart = addDays(thisWeekStart, -7);
  const lastMonth = addMonths(today, -1);
  const thisQuarterStart = startOfQuarter(today);
  const lastQuarterDate = addMonths(thisQuarterStart, -3);

  const ranges = {
    Today: { start: today, end: today },
    Yesterday: { start: yesterday, end: yesterday },
    "This Week": { start: thisWeekStart, end: today },
    "Last Week": { start: lastWeekStart, end: addDays(thisWeekStart, -1) },
    "Last 7 Days": { start: addDays(today, -7), end: today },
    "Last 15 Days": { start: addDays(today, -15), end: today },
    "Last 30 Days": { start: addDays(today, -30), end: today },
    "Last 90 Days": { start: addDays(today, -90), end: today },
    "This Month": { start: startOfMonth(today), end: today },
    "Last Month": { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) },
    "This Quarter": { start: thisQuarterStart, end: today },
    "Last Quarter": {
      start: startOfQuarter(lastQuarterDate),
      end: endOfQuarter(lastQuarterDate),
    },
    "This Year": { start: new Date(today.getFullYear(), 0, 1), end: today },
    "Last Year": {
      start: new Date(today.getFullYear() - 1, 0, 1),
      end: new Date(today.getFullYear() - 1, 11, 31),
    },
  };

  const resolved = ranges[preset] || ranges["This Week"];
  return {
    startDate: toIsoDate(resolved.start),
    endDate: toIsoDate(resolved.end),
    preset: ranges[preset] ? preset : "This Week",
  };
}

export function createDefaultQmsDashboardRange() {
  return {
    preset: "This Week",
    ...resolveQmsDashboardDateRange({ preset: "This Week" }),
  };
}

function extractDashboardTable(response, tableName) {
  const fromApi = getApiTable(response, tableName);
  if (fromApi.length) return fromApi;

  const root = response?.[tableName];
  if (Array.isArray(root)) return root;

  const payload = getApiData(response);
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const match = Object.keys(payload).find(
      (key) => key.toLowerCase() === String(tableName || "").toLowerCase(),
    );
    if (match && Array.isArray(payload[match])) return payload[match];
  }

  return [];
}

function parseCount(row, ...keys) {
  const raw = pickApiRowValue(row, ...keys);
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function groupReadUnreadRows(rows, labelKeys) {
  const map = new Map();

  for (const row of rows || []) {
    const label =
      labelKeys.map((key) => pickApiRowValue(row, key)).find(Boolean) || "Unknown";
    const count = parseCount(row, "ReadDocuments", "readDocuments");
    const isRead = parseCount(row, "ReadDoc", "readDoc") === 1;
    const color = pickApiRowValue(row, "ColorValue", "colorValue") || "";

    if (!map.has(label)) {
      map.set(label, { label, read: 0, unread: 0, color, total: 0, pct: 0 });
    }
    const item = map.get(label);
    if (isRead) item.read += count;
    else item.unread += count;
    item.total = item.read + item.unread;
    if (color && !item.color) item.color = color;
  }

  return Array.from(map.values())
    .map((row) => ({
      ...row,
      pct: row.total > 0 ? Math.round((row.read / row.total) * 100) : 0,
    }))
    .sort((a, b) => b.total - a.total);
}

function parseTimelineRows(rows) {
  return (rows || []).map((row) => {
    const read = parseCount(row, "ReadDocuments", "readDocuments");
    const unread = parseCount(row, "UnreadDocuments", "unreadDocuments");
    const rawDate = pickApiRowValue(row, "DateValue", "dateValue") || "";
    const total = read + unread;
    return {
      dateLabel: rawDate,
      read,
      unread,
      total,
      pct: total > 0 ? Math.round((read / total) * 100) : 0,
    };
  });
}

function buildSummary(timeline, breakdowns) {
  const fromTimeline = timeline.reduce(
    (acc, row) => {
      acc.read += row.read;
      acc.unread += row.unread;
      return acc;
    },
    { read: 0, unread: 0 },
  );

  let read = fromTimeline.read;
  let unread = fromTimeline.unread;

  if (read + unread === 0) {
    for (const list of breakdowns) {
      for (const row of list) {
        read += row.read;
        unread += row.unread;
      }
    }
  }

  const total = read + unread;
  const completionRate = total > 0 ? Math.round((read / total) * 100) : 0;
  const activeDays = timeline.filter((row) => row.total > 0).length;
  const typesWithPending = breakdowns[0]?.filter((r) => r.unread > 0).length || 0;
  const rolesWithPending = breakdowns[2]?.filter((r) => r.unread > 0).length || 0;
  const staffWithPending = breakdowns[3]?.filter((r) => r.unread > 0).length || 0;

  return {
    read,
    unread,
    total,
    completionRate,
    activeDays,
    rangeDays: timeline.length,
    typesWithPending,
    rolesWithPending,
    staffWithPending,
  };
}

export function buildQmsDashboardModel(response) {
  const timeline = parseTimelineRows(
    extractDashboardTable(response, "DocumentReadyUnreadDatewise"),
  );
  const byType = groupReadUnreadRows(
    extractDashboardTable(response, "DocumentReadyUnreadDocumentTypewise"),
    ["DocumentType", "documentType"],
  );
  const byCategory = groupReadUnreadRows(
    extractDashboardTable(response, "DocumentReadyUnreadDocumentCategorywise"),
    ["DocumentCategory", "documentCategory"],
  );
  const byRole = groupReadUnreadRows(
    extractDashboardTable(response, "DocumentReadyUnreadStaffRolewise"),
    ["Roles", "roles"],
  );
  const byStaff = groupReadUnreadRows(
    extractDashboardTable(response, "DocumentReadyUnreadStaffwise"),
    ["StaffName", "staffName"],
  );

  const summary = buildSummary(timeline, [byType, byCategory, byRole, byStaff]);

  return {
    timeline,
    byType,
    byCategory,
    byRole,
    byStaff,
    summary,
  };
}

export function buildAtRiskFeed(model) {
  if (!model) return [];
  const tagged = [
    ...model.byType.map((row) => ({ ...row, dimension: "Document type" })),
    ...model.byCategory.map((row) => ({ ...row, dimension: "Category" })),
    ...model.byRole.map((row) => ({ ...row, dimension: "Staff role" })),
    ...(model.byStaff || []).map((row) => ({ ...row, dimension: "Staff" })),
  ];
  return tagged
    .filter((row) => row.unread > 0)
    .sort((a, b) => b.unread - a.unread || b.pct - a.pct)
    .slice(0, 9);
}

export function buildDetailRows(model, { dimension = "all", sel = null } = {}) {
  if (!model) return [];
  let rows = [];
  if (dimension === "all" || dimension === "type") {
    rows = rows.concat(model.byType.map((row) => ({ ...row, dimension: "Document type" })));
  }
  if (dimension === "all" || dimension === "category") {
    rows = rows.concat(model.byCategory.map((row) => ({ ...row, dimension: "Category" })));
  }
  if (dimension === "all" || dimension === "role") {
    rows = rows.concat(model.byRole.map((row) => ({ ...row, dimension: "Staff role" })));
  }
  if (dimension === "all" || dimension === "staff") {
    rows = rows.concat(
      (model.byStaff || []).map((row) => ({ ...row, dimension: "Staff" })),
    );
  }

  if (sel?.k === "read") rows = rows.filter((row) => row.read > 0);
  else if (sel?.k === "unread") rows = rows.filter((row) => row.unread > 0);
  else if (sel?.k === "low") rows = rows.filter((row) => row.pct < 70 && row.total > 0);
  else if (sel?.label) rows = rows.filter((row) => row.label === sel.label);
  else if (sel?.dimension) rows = rows.filter((row) => row.dimension === sel.dimension);

  return rows.sort((a, b) => b.unread - a.unread || b.total - a.total);
}

export function getBreakdownList(model, tabId) {
  if (!model) return [];
  if (tabId === "1") return model.byType;
  if (tabId === "2") return model.byCategory;
  if (tabId === "4") return model.byRole;
  if (tabId === "5") return model.byStaff || [];
  if (tabId === "3") {
    return [...model.byType, ...model.byCategory, ...model.byRole, ...(model.byStaff || [])]
      .filter((row) => row.unread > 0)
      .sort((a, b) => b.unread - a.unread);
  }
  return model.byType;
}

export function formatDashboardDateLabel(isoDate) {
  if (!isoDate) return "—";
  const parts = String(isoDate).split(/[/-]/);
  if (parts.length === 3 && parts[0].length <= 2) {
    return isoDate;
  }
  const dt = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return isoDate;
  return dt.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
